import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { api } from '@/lib/api'
import type {
  ContactEnrichment,
  ContactEnrichmentListResponse,
  ApplyEnrichmentResponse,
} from '@/lib/types'

export function useContactEnrichments(contactId: string | undefined) {
  return useQuery({
    queryKey: ['contact-enrichments', contactId],
    queryFn: () =>
      api<ContactEnrichmentListResponse>(`/api/contacts/${contactId}/enrichments`).then(
        (r) => r.items,
      ),
    enabled: !!contactId,
  })
}

export function useTriggerEnrichment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, provider = 'pdl' }: { contactId: string; provider?: string }) =>
      api<ContactEnrichment>(`/api/contacts/${contactId}/enrich`, {
        method: 'POST',
        body: JSON.stringify({ provider }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['contact-enrichments', vars.contactId] })
      if (_data.status === 'completed') {
        toast.success('Enrichment data found')
      } else if (_data.status === 'failed') {
        toast.error(_data.error || 'No data found')
      } else {
        toast.success('Partial data found')
      }
    },
    onError: () => {
      toast.error('Failed to enrich contact')
    },
  })
}

export function useApplyEnrichment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      enrichmentId,
      fields,
    }: {
      contactId: string
      enrichmentId: string
      fields: string[]
    }) =>
      api<ApplyEnrichmentResponse>(`/api/contacts/${contactId}/enrichments/${enrichmentId}/apply`, {
        method: 'POST',
        body: JSON.stringify({ fields }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['contact-enrichments', vars.contactId] })
      qc.invalidateQueries({ queryKey: ['contact', vars.contactId] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(`Applied ${_data.appliedFields.length} fields`)
    },
    onError: () => {
      toast.error('Failed to apply enrichment')
    },
  })
}
